/**
 * T-shape Cushion Configurator — Pricing Engine + Interactive SVG Preview
 * uscushion.com · 2026-07
 *
 * T-shape cushion = single piece with symmetric notches on both sides
 * 5 dimensions: Length (total depth), Front Width, Back Width, Ear (wing depth), Thickness
 * Back Width > Front Width → two symmetric notches
 * Pricing: bounding rectangle (Back Width × Length × Thickness)
 */
(function () {
  'use strict';

  /* ─── Cost constants (same as bench) ─── */
  var FABRIC_PRICE_RMB  = 25;
  var FOAM_PRICE_RMB    = 1750;
  var FOAM_DENSITY      = 35;
  var FABRIC_GSM        = 400;
  var SHIPPING_RMB      = 90;
  var ACCESSORIES_RMB   = 30;
  var EXCHANGE_RATE     = 6.8;
  var PAYMENT_FEE_RATE  = 0.049;
  var TARGET_COST_RATIO = 0.40;
  var MIN_PRICE         = 45;

  /* ─── Addon pricing (USD) ─── */
  var ADDON = {
    shape:       { tshape: 0, tshape_tufted: 30, tshape_france_tufted: 30 },
    piping:      { none: 0, single: 5, double: 8 },
    pipingColor: { match: 0, white: 5, black: 5, navy: 5, cream: 5 },
    straps:      { none: 0, two: 2, four: 4 },
    zipper:      { regular: 0, invisible: 0 },
    antislip:    { none: 0, black_pad: 5, beige_pad: 5, velcro: 9 },
    extraCovers: { '0': 0, '1': 30, '2': 50 },
    throwPillow: {
      none: 0,
      '2pcs_12x20': 29, '2pcs_16x16': 29, '2pcs_18x18': 32,
      '2pcs_20x20': 35, '2pcs_24x24': 38, '2pcs_custom': 40,
      '4pcs_12x20': 51, '4pcs_16x16': 51, '4pcs_18x18': 57,
      '4pcs_20x20': 63, '4pcs_24x24': 67, '4pcs_custom': 70
    },
    pillowCovers: {
      none: 0,
      '2pcs_12x20': 19, '2pcs_16x16': 19, '2pcs_18x18': 21,
      '2pcs_20x20': 24, '2pcs_24x24': 29, '2pcs_custom': 31,
      '4pcs_12x20': 32, '4pcs_16x16': 32, '4pcs_18x18': 36,
      '4pcs_20x20': 42, '4pcs_24x24': 52, '4pcs_custom': 55
    },
    armrestPillow: { none: 0, square_2pcs: 30, round_2pcs: 30 }
  };

  var LABELS = {
    tshape: 'T-shape', tshape_tufted: 'Tufted T-shape', tshape_france_tufted: 'France tufted T-shape',
    none: 'None', single: 'Single piping', double: 'Double piping',
    match: 'Match fabric', white: 'White piping', black: 'Black piping', navy: 'Navy piping', cream: 'Cream piping',
    two: '2 straps', four: '4 straps',
    regular: 'Regular zipper', invisible: 'Invisible zipper',
    velcro: 'Velcro', black_pad: 'Black pad', beige_pad: 'Beige pad'
  };

  var FABRIC_COLORS = {
    'blue':'#2c3e6b','navy':'#1a2540','teal':'#2a7a6b','green':'#4a7a2a',
    'sage':'#a8b89a','red':'#b83232','coral':'#d85a45','orange':'#d4783a',
    'yellow':'#d4a83a','gold':'#c8963a','pink':'#d4649a','purple':'#6a4a9a',
    'lavender':'#9a8ab8','brown':'#7a5a3a','camel':'#c8a878','tan':'#c8b08a',
    'beige':'#d4c8a8','cream':'#ede8df','ivory':'#f0ead8','white':'#f5f5f0',
    'gray':'#9a9a9a','grey':'#9a9a9a','charcoal':'#4a4a4a','black':'#1a1a1a',
    'mint':'#98d4b4','rust':'#b7502a','mustard':'#c8a020','olive':'#6b702a',
    'blush':'#de9898','mauve':'#b07898','burgundy':'#6b1a2a','taupe':'#8a7a6a',
    'sand':'#c8b898','silver':'#bfc0c2','steel blue':'#4a7a98',
    'dark olive':'#4a4a2a','dark green':'#2a5a2a','dark brown':'#4a3020',
    'dark blue':'#1a2a5a','dark teal':'#1a5a5a','dark gray':'#5a5a5a',
    'light gray':'#c8c8c8','gray blue':'#6a7a8a','yellow green':'#8ab82a',
    'gray black':'#3a3a3a'
  };

  /* ─── State ─── */
  var state = {
    shape: '',
    length: 0, frontW: 0, backW: 0, ear: 0, thickness: 0,
    fabric: null, piping: '', pipingColor: 'match', straps: '',
    zipper: 'regular', zipperPlacement: '', antislip: '',
    extraCovers: '0', throwPillow: 'none', pillowCovers: 'none',
    armrestPillow: 'none', notes: ''
  };

  var els = {};

  /* ─── Color helpers ─── */
  function hexToRgb(hex) {
    hex = hex.replace('#', '');
    return [parseInt(hex.substring(0, 2), 16), parseInt(hex.substring(2, 4), 16), parseInt(hex.substring(4, 6), 16)];
  }
  function rgbToHex(r, g, b) {
    return '#' + [r, g, b].map(function (c) {
      var x = Math.round(Math.max(0, Math.min(255, c))).toString(16);
      return x.length === 1 ? '0' + x : x;
    }).join('');
  }
  function lighten(hex, r) { var c = hexToRgb(hex); return rgbToHex(c[0] + (255 - c[0]) * r, c[1] + (255 - c[1]) * r, c[2] + (255 - c[2]) * r); }
  function darken(hex, r)  { var c = hexToRgb(hex); return rgbToHex(c[0] * (1 - r), c[1] * (1 - r), c[2] * (1 - r)); }
  function extractTag(tags, prefix) {
    if (!tags) return '';
    for (var i = 0; i < tags.length; i++) {
      var t = (typeof tags[i] === 'string' ? tags[i] : '').toLowerCase().trim();
      if (t.indexOf(prefix + ':') === 0) return tags[i].split(':')[1].trim().toLowerCase();
    }
    return '';
  }

  /* ─── Pricing (bounding rectangle: backW × length × thickness) ─── */
  function calcBasePrice(L, W, T) {
    if (!L || !W || !T) return null;
    var Lm = L / 39.37, Wm = W / 39.37, Tm = T / 39.37;
    var fabricArea = Lm * Wm * 2 + Lm * Tm * 2 + Wm * Tm * 2;
    var foamVolume = Lm * Wm * Tm;
    var totalWeight = foamVolume * FOAM_DENSITY + fabricArea * FABRIC_GSM / 1000;
    var totalRMB = fabricArea * FABRIC_PRICE_RMB + foamVolume * FOAM_PRICE_RMB + totalWeight * SHIPPING_RMB + ACCESSORIES_RMB;
    return Math.max(totalRMB / EXCHANGE_RATE / (1 - PAYMENT_FEE_RATE) / TARGET_COST_RATIO * (window.__CC_COVER_FACTOR || 1), MIN_PRICE);
  }

  function getAddonTotal() {
    return (ADDON.shape[state.shape] || 0)
      + (ADDON.piping[state.piping] || 0)
      + (ADDON.pipingColor[state.pipingColor] || 0)
      + (ADDON.straps[state.straps] || 0) + (ADDON.zipper[state.zipper] || 0)
      + (ADDON.antislip[state.antislip] || 0) + (ADDON.extraCovers[state.extraCovers] || 0)
      + (ADDON.throwPillow[state.throwPillow] || 0)
      + (ADDON.pillowCovers[state.pillowCovers] || 0)
      + (ADDON.armrestPillow[state.armrestPillow] || 0);
  }

  function hasDims() {
    return state.length >= 6 && state.frontW >= 6 && state.backW >= 6
      && state.ear >= 1 && state.thickness >= 1;
  }

  function isComplete() {
    return state.shape && hasDims()
      && state.fabric && state.piping && state.straps && state.antislip;
  }

  /* ═══════════════════════════════════════════════════════════
     SVG Preview — T-shape cushion in isometric projection
     Uses isoProject approach (same as L-shape configurator).

     Plan view (x = width right, y = depth back):

        P5 ──────────────────────── P4
        │        Back Width          │
        │                            │
        P6 ──── P7          P2 ──── P3
                 │          │
                 │  Front   │
                 │  Width   │
                 │          │
                 P0 ────── P1

     earW = (backW − frontW) / 2 on each side
     stem = length − ear (front section depth)
     ═══════════════════════════════════════════════════════════ */

  function drawCushion() {
    var svg = els.dimSvg;
    if (!svg) return;

    var uLen = state.length, uFW = state.frontW, uBW = state.backW, uEar = state.ear, uT = state.thickness;
    var empty = (!uLen && !uFW && !uBW && !uEar && !uT);

    function clamp(v, mn, mx) { return Math.max(mn, Math.min(mx, v)); }
    function pt(a) { return a[0].toFixed(1) + ',' + a[1].toFixed(1); }
    function f(n)  { return n.toFixed(1); }

    /* ── Resolve face colors from fabric ── */
    var topC = '#EDE9E0', frontC = '#D5D0C2', sideC = '#E4E0D4', innerC = '#DDD8CA', strokeC = '#2A2A28';
    var fabricLum = 200;
    if (state.fabric && state.fabric.tags) {
      var colorTag = extractTag(state.fabric.tags, 'color');
      var baseHex = FABRIC_COLORS[colorTag];
      if (baseHex) {
        topC = lighten(baseHex, 0.50);
        sideC = lighten(baseHex, 0.35);
        frontC = lighten(baseHex, 0.20);
        innerC = lighten(baseHex, 0.28);
        strokeC = darken(baseHex, 0.45);
        var sc = hexToRgb(strokeC);
        fabricLum = (sc[0] + sc[1] + sc[2]) / 3;
        if (fabricLum > 120) strokeC = '#2A2A28';
        var fc = hexToRgb(baseHex);
        fabricLum = (fc[0] + fc[1] + fc[2]) / 3;
      }
    }

    var h = '';

    /* ── Defs ── */
    h += '<defs><marker id="ts-arr" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="5" markerHeight="5" orient="auto-start-reverse">'
      + '<path d="M1 1.5L8.5 5L1 8.5" fill="none" stroke="context-stroke" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></marker></defs>';

    /* ── Isometric direction vectors ── */
    var dirHoriz = [0.98776, -0.15596];
    var dirDiag  = [-0.35120, -0.93660];

    /* ── Compute pixel sizes ── */
    var backWPx, frontWPx, lengthPx, earPx, seatH, earWPx, stemPx;
    if (empty) {
      backWPx = 180; frontWPx = 100; lengthPx = 130; earPx = 40; seatH = 14;
    } else {
      var eBW = uBW || 60, eFW = uFW || 36, eLen = uLen || 40, eEar = uEar || 12, eT = uT || 3;
      var scale = clamp(180 / eBW, 1.5, 5);
      backWPx  = clamp(eBW * scale, 100, 280);
      frontWPx = clamp(eFW * scale, 50, 250);
      lengthPx = clamp(eLen * scale * 1.1, 60, 200);
      earPx    = clamp(eEar * scale * 1.1, 15, lengthPx * 0.8);
      seatH    = clamp(eT * 5, 8, 35);
    }
    earWPx = Math.max((backWPx - frontWPx) / 2, 0);
    stemPx = Math.max(lengthPx - earPx, 5);

    var origin = [200, 280];

    function isoProject(px, py) {
      return [
        origin[0] + px * dirHoriz[0] + py * dirDiag[0],
        origin[1] + px * dirHoriz[1] + py * dirDiag[1]
      ];
    }

    /* ── Plan vertices (clockwise from front-left) ── */
    var planVerts = [
      [earWPx, 0],                        // P0: front-left
      [earWPx + frontWPx, 0],             // P1: front-right
      [earWPx + frontWPx, stemPx],        // P2: right notch inner
      [backWPx, stemPx],                  // P3: right ear bottom
      [backWPx, lengthPx],               // P4: back-right
      [0, lengthPx],                      // P5: back-left
      [0, stemPx],                        // P6: left ear bottom
      [earWPx, stemPx]                    // P7: left notch inner
    ];

    var top = planVerts.map(function (p) { return isoProject(p[0], p[1]); });
    var bot = top.map(function (p) { return [p[0], p[1] + seatH]; });

    /* ── Auto-fit viewBox ── */
    var allPts = top.concat(bot);
    var minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
    for (var i = 0; i < allPts.length; i++) {
      if (allPts[i][0] < minX) minX = allPts[i][0];
      if (allPts[i][0] > maxX) maxX = allPts[i][0];
      if (allPts[i][1] < minY) minY = allPts[i][1];
      if (allPts[i][1] > maxY) maxY = allPts[i][1];
    }
    var mrgL = 75, mrgR = 60, mrgT = 40, mrgB = 50;
    var vw = Math.ceil(maxX - minX + mrgL + mrgR);
    var vh = Math.ceil(maxY - minY + mrgT + mrgB);
    var vx = Math.floor(minX - mrgL);
    var vy = Math.floor(minY - mrgT);
    svg.setAttribute('viewBox', vx + ' ' + vy + ' ' + vw + ' ' + vh);

    /* ── Straps (drawn behind cushion) ── */
    if ((state.straps === 'two' || state.straps === 'four') && !empty) {
      var tieC = fabricLum < 100 ? '#8A8A85' : '#4A4A47';
      var hangLen = 16;
      function drawTies(cx_, cy_, dirX) {
        var spread = 4;
        h += '<path d="M' + f(cx_ - spread * 0.5) + ' ' + f(cy_)
          + ' Q' + f(cx_ - spread + dirX * 3) + ' ' + f(cy_ + hangLen * 0.5)
          + ' ' + f(cx_ - spread * 1.5 + dirX * 5) + ' ' + f(cy_ + hangLen)
          + '" fill="none" stroke="' + tieC + '" stroke-width="1.6" stroke-linecap="round"/>';
        h += '<path d="M' + f(cx_ + spread * 0.5) + ' ' + f(cy_)
          + ' Q' + f(cx_ + spread + dirX * 2) + ' ' + f(cy_ + hangLen * 0.5)
          + ' ' + f(cx_ + spread * 1.5 + dirX * 4) + ' ' + f(cy_ + hangLen)
          + '" fill="none" stroke="' + tieC + '" stroke-width="1.6" stroke-linecap="round"/>';
      }
      drawTies(bot[0][0], bot[0][1], -1);
      drawTies(bot[1][0], bot[1][1], 1);
      if (state.straps === 'four') {
        drawTies(bot[5][0], bot[5][1], -1);
        drawTies(bot[4][0], bot[4][1], 1);
      }
    }

    /* ── Thickness faces (painter's algorithm: far → near) ──
       Visible faces (normals point toward viewer):
       P3→P4: right ear side → sideC
       P5→P6: left ear side → sideC
       P2→P3: right notch step → innerC (faces forward)
       P6→P7: left notch step → innerC (faces forward)
       P1→P2: right stem side → sideC
       P7→P0: left stem side → sideC
       P0→P1: front edge → frontC
    */

    // 1. Right ear side (P3→P4) — far back-right
    h += '<polygon points="' + [top[3], top[4], bot[4], bot[3]].map(pt).join(' ')
      + '" fill="' + sideC + '" stroke="' + strokeC + '" stroke-width="1.4" stroke-linejoin="round"/>';

    // 2. Left ear side (P5→P6) — far back-left
    h += '<polygon points="' + [top[5], top[6], bot[6], bot[5]].map(pt).join(' ')
      + '" fill="' + sideC + '" stroke="' + strokeC + '" stroke-width="1.4" stroke-linejoin="round"/>';

    // 3. Right notch step (P2→P3) — inner face, faces forward
    h += '<polygon points="' + [top[2], top[3], bot[3], bot[2]].map(pt).join(' ')
      + '" fill="' + innerC + '" stroke="' + strokeC + '" stroke-width="1.4" stroke-linejoin="round"/>';

    // 4. Left notch step (P6→P7) — inner face, faces forward
    h += '<polygon points="' + [top[6], top[7], bot[7], bot[6]].map(pt).join(' ')
      + '" fill="' + innerC + '" stroke="' + strokeC + '" stroke-width="1.4" stroke-linejoin="round"/>';

    // 5. Right stem side (P1→P2)
    h += '<polygon points="' + [top[1], top[2], bot[2], bot[1]].map(pt).join(' ')
      + '" fill="' + sideC + '" stroke="' + strokeC + '" stroke-width="1.4" stroke-linejoin="round"/>';

    // 6. Left stem side (P7→P0)
    h += '<polygon points="' + [top[7], top[0], bot[0], bot[7]].map(pt).join(' ')
      + '" fill="' + sideC + '" stroke="' + strokeC + '" stroke-width="1.4" stroke-linejoin="round"/>';

    // 7. Top face (T-shape polygon)
    h += '<polygon points="' + top.map(pt).join(' ')
      + '" fill="' + topC + '" stroke="' + strokeC + '" stroke-width="1.4" stroke-linejoin="round"/>';

    // 7b. Tufted dots on top face
    if ((state.shape === 'tshape_tufted' || state.shape === 'tshape_france_tufted') && !empty) {
      var dotC = darken(topC, 0.18);
      var cx = 0, cy = 0;
      for (var di = 0; di < top.length; di++) { cx += top[di][0]; cy += top[di][1]; }
      cx /= top.length; cy /= top.length;
      var dotR = 2.2;
      var cols = 3, rows = 4;
      for (var dr = 0; dr < rows; dr++) {
        for (var dc = 0; dc < cols; dc++) {
          var fx = (dc + 1) / (cols + 1);
          var fy = (dr + 1) / (rows + 1);
          var dx = top[0][0] + (top[1][0] - top[0][0]) * fx + (top[5][0] - top[0][0]) * fy;
          var dy = top[0][1] + (top[1][1] - top[0][1]) * fx + (top[5][1] - top[0][1]) * fy;
          h += '<circle cx="' + f(dx) + '" cy="' + f(dy) + '" r="' + dotR + '" fill="' + dotC + '" opacity="0.5"/>';
        }
      }
    }

    // 8. Front edge (P0→P1) — closest to viewer
    h += '<polygon points="' + [top[0], top[1], bot[1], bot[0]].map(pt).join(' ')
      + '" fill="' + frontC + '" stroke="' + strokeC + '" stroke-width="1.4" stroke-linejoin="round"/>';

    /* ── Piping ── */
    if (state.piping === 'single' || state.piping === 'double') {
      var pipC = '#F5F1E8', pw = 2.2;
      h += '<polygon points="' + top.map(pt).join(' ')
        + '" fill="none" stroke="' + pipC + '" stroke-width="' + pw + '" stroke-linejoin="round" stroke-linecap="round" opacity="0.85"/>';
      if (state.piping === 'double') {
        h += '<polyline points="' + [bot[5], bot[6], bot[7], bot[0], bot[1], bot[2], bot[3]].map(pt).join(' ')
          + '" fill="none" stroke="' + pipC + '" stroke-width="' + pw + '" stroke-linejoin="round" stroke-linecap="round" opacity="0.85"/>';
      }
    }

    /* ── Anti-slip on bottom ── */
    if (state.antislip && state.antislip !== 'none' && !empty) {
      var padC, padOp;
      if (state.antislip === 'black_pad') { padC = '#1A1A18'; padOp = 0.32; }
      else if (state.antislip === 'beige_pad') { padC = '#C8B898'; padOp = 0.4; }
      else { padC = '#4A4020'; padOp = 0.26; }

      var padOffset = 1, padThick = 4, padInset = 5;
      var normalDir = [-dirHoriz[1], dirHoriz[0]];
      var edgeLenPx = Math.sqrt(Math.pow(bot[1][0] - bot[0][0], 2) + Math.pow(bot[1][1] - bot[0][1], 2));

      function stripQuad(p1, p2, t0, t1) {
        var elen = Math.sqrt(Math.pow(p2[0] - p1[0], 2) + Math.pow(p2[1] - p1[1], 2));
        var q1 = [p1[0] + (p2[0] - p1[0]) * (t0 / elen), p1[1] + (p2[1] - p1[1]) * (t0 / elen)];
        var q2 = [p1[0] + (p2[0] - p1[0]) * (t1 / elen), p1[1] + (p2[1] - p1[1]) * (t1 / elen)];
        var q1o = [q1[0] + normalDir[0] * padOffset, q1[1] + normalDir[1] * padOffset];
        var q2o = [q2[0] + normalDir[0] * padOffset, q2[1] + normalDir[1] * padOffset];
        var q3 = [q2[0] + normalDir[0] * (padOffset + padThick), q2[1] + normalDir[1] * (padOffset + padThick)];
        var q4 = [q1[0] + normalDir[0] * (padOffset + padThick), q1[1] + normalDir[1] * (padOffset + padThick)];
        return [q1o, q2o, q3, q4];
      }

      if (state.antislip === 'velcro') {
        var strips = 4, gapPx = edgeLenPx / strips;
        for (var vi = 0; vi < strips; vi++) {
          var t0 = vi * gapPx + 3, t1 = (vi + 1) * gapPx - 3;
          if (t1 <= t0) continue;
          var quad = stripQuad(bot[0], bot[1], t0, t1);
          h += '<polygon points="' + quad.map(pt).join(' ') + '" fill="' + padC + '" opacity="' + padOp + '"/>';
        }
      } else {
        var quad = stripQuad(bot[0], bot[1], padInset, edgeLenPx - padInset);
        h += '<polygon points="' + quad.map(pt).join(' ') + '" fill="' + padC + '" opacity="' + padOp + '"/>';
      }
    }

    /* ── Dimension labels ── */
    var dimC = '#1a4f78', thkC = '#3d5e3f';
    var ls = 10;
    var hug = 20;

    var lblLen = uLen ? ' — ' + uLen + '″' : '';
    var lblFW  = uFW  ? ' — ' + uFW  + '″' : '';
    var lblBW  = uBW  ? ' — ' + uBW  + '″' : '';
    var lblEar = uEar ? ' — ' + uEar + '″' : '';
    var lblT   = uT   ? ' — ' + uT   + '″' : '';

    // Front Width — below front edge (P0→P1)
    var fwa1 = [bot[0][0], bot[0][1] + hug];
    var fwa2 = [bot[1][0], bot[1][1] + hug];
    h += '<line x1="' + f(bot[0][0]) + '" y1="' + f(bot[0][1]) + '" x2="' + f(fwa1[0]) + '" y2="' + f(fwa1[1]) + '" stroke="' + dimC + '" stroke-width="0.6"/>';
    h += '<line x1="' + f(bot[1][0]) + '" y1="' + f(bot[1][1]) + '" x2="' + f(fwa2[0]) + '" y2="' + f(fwa2[1]) + '" stroke="' + dimC + '" stroke-width="0.6"/>';
    h += '<line x1="' + f(fwa1[0]) + '" y1="' + f(fwa1[1]) + '" x2="' + f(fwa2[0]) + '" y2="' + f(fwa2[1])
      + '" stroke="' + dimC + '" stroke-width="1.3" marker-start="url(#ts-arr)" marker-end="url(#ts-arr)"/>';
    h += '<text x="' + f((fwa1[0] + fwa2[0]) / 2) + '" y="' + f((fwa1[1] + fwa2[1]) / 2 + 14)
      + '" text-anchor="middle" font-size="' + ls + '" font-family="Poppins,sans-serif" font-weight="600" fill="' + dimC
      + '">Front Width' + lblFW + '</text>';

    // Back Width — above back edge (P5→P4)
    var bwa1 = [top[5][0] + dirDiag[0] * 15, top[5][1] + dirDiag[1] * 15];
    var bwa2 = [top[4][0] + dirDiag[0] * 15, top[4][1] + dirDiag[1] * 15];
    h += '<line x1="' + f(top[5][0]) + '" y1="' + f(top[5][1]) + '" x2="' + f(bwa1[0]) + '" y2="' + f(bwa1[1]) + '" stroke="' + dimC + '" stroke-width="0.6"/>';
    h += '<line x1="' + f(top[4][0]) + '" y1="' + f(top[4][1]) + '" x2="' + f(bwa2[0]) + '" y2="' + f(bwa2[1]) + '" stroke="' + dimC + '" stroke-width="0.6"/>';
    h += '<line x1="' + f(bwa1[0]) + '" y1="' + f(bwa1[1]) + '" x2="' + f(bwa2[0]) + '" y2="' + f(bwa2[1])
      + '" stroke="' + dimC + '" stroke-width="1.3" marker-start="url(#ts-arr)" marker-end="url(#ts-arr)"/>';
    h += '<text x="' + f((bwa1[0] + bwa2[0]) / 2) + '" y="' + f((bwa1[1] + bwa2[1]) / 2 - 5)
      + '" text-anchor="middle" font-size="' + ls + '" font-family="Poppins,sans-serif" font-weight="600" fill="' + dimC
      + '">Back Width' + lblBW + '</text>';

    // Length — on the top face, from front midpoint to back midpoint
    var frontMid = [(top[0][0] + top[1][0]) / 2, (top[0][1] + top[1][1]) / 2];
    var backMid  = [(top[5][0] + top[4][0]) / 2, (top[5][1] + top[4][1]) / 2];
    h += '<line x1="' + f(frontMid[0]) + '" y1="' + f(frontMid[1]) + '" x2="' + f(backMid[0]) + '" y2="' + f(backMid[1])
      + '" stroke="' + dimC + '" stroke-width="1.3" marker-start="url(#ts-arr)" marker-end="url(#ts-arr)"/>';
    var lenMid = [(frontMid[0] + backMid[0]) / 2, (frontMid[1] + backMid[1]) / 2];
    h += '<text x="' + f(lenMid[0] + 8) + '" y="' + f(lenMid[1] - 4)
      + '" text-anchor="start" font-size="' + ls + '" font-family="Poppins,sans-serif" font-weight="600" fill="' + dimC
      + '">Length' + lblLen + '</text>';

    // Ear — right side, from P3 to P4 (ear depth on top face)
    var earA1 = [top[3][0] + dirHoriz[0] * hug, top[3][1] + dirHoriz[1] * hug];
    var earA2 = [top[4][0] + dirHoriz[0] * hug, top[4][1] + dirHoriz[1] * hug];
    h += '<line x1="' + f(top[3][0]) + '" y1="' + f(top[3][1]) + '" x2="' + f(earA1[0]) + '" y2="' + f(earA1[1]) + '" stroke="' + dimC + '" stroke-width="0.6"/>';
    h += '<line x1="' + f(top[4][0]) + '" y1="' + f(top[4][1]) + '" x2="' + f(earA2[0]) + '" y2="' + f(earA2[1]) + '" stroke="' + dimC + '" stroke-width="0.6"/>';
    h += '<line x1="' + f(earA1[0]) + '" y1="' + f(earA1[1]) + '" x2="' + f(earA2[0]) + '" y2="' + f(earA2[1])
      + '" stroke="' + dimC + '" stroke-width="1.3" marker-start="url(#ts-arr)" marker-end="url(#ts-arr)"/>';
    var earMid = [(earA1[0] + earA2[0]) / 2, (earA1[1] + earA2[1]) / 2];
    h += '<text x="' + f(earMid[0] + 6) + '" y="' + f(earMid[1] + 4)
      + '" text-anchor="start" font-size="' + ls + '" font-family="Poppins,sans-serif" font-weight="600" fill="' + dimC
      + '">Ear' + lblEar + '</text>';

    // Thickness — vertical at front-right corner (P1)
    var thx = bot[1][0] + hug;
    h += '<line x1="' + f(top[1][0]) + '" y1="' + f(top[1][1]) + '" x2="' + f(thx) + '" y2="' + f(top[1][1]) + '" stroke="' + thkC + '" stroke-width="0.6"/>';
    h += '<line x1="' + f(bot[1][0]) + '" y1="' + f(bot[1][1]) + '" x2="' + f(thx) + '" y2="' + f(bot[1][1]) + '" stroke="' + thkC + '" stroke-width="0.6"/>';
    h += '<line x1="' + f(thx) + '" y1="' + f(top[1][1] + 2) + '" x2="' + f(thx) + '" y2="' + f(bot[1][1] - 2)
      + '" stroke="' + thkC + '" stroke-width="1.3" marker-start="url(#ts-arr)" marker-end="url(#ts-arr)"/>';
    h += '<text x="' + f(thx + 5) + '" y="' + f((top[1][1] + bot[1][1]) / 2 + 4)
      + '" text-anchor="start" font-size="' + ls + '" font-family="Poppins,sans-serif" font-weight="600" fill="' + thkC
      + '">T' + lblT + '</text>';

    if (empty) {
      var hintX = (bot[0][0] + bot[1][0]) / 2;
      var hintY = Math.max(fwa1[1], fwa2[1]) + 26;
      h += '<text x="' + f(hintX) + '" y="' + f(hintY) + '" text-anchor="middle" font-size="10.5" font-family="Poppins,sans-serif" fill="#8A8A85">Enter dimensions below to see your cushion</text>';
    }

    svg.innerHTML = h;
  }

  /* ─── Summary ─── */
  function updateSummary() {
    if (!els.summary) return;
    var parts = [];
    if (state.shape) parts.push(LABELS[state.shape] || state.shape);
    if (state.length && state.frontW && state.backW && state.ear && state.thickness)
      parts.push(state.backW + '×' + state.length + '×' + state.thickness + '″');
    if (state.fabric) parts.push(state.fabric.title);
    if (state.piping && state.piping !== 'none') {
      var pipLabel = LABELS[state.piping];
      if (state.pipingColor && state.pipingColor !== 'match') pipLabel += ' (' + (LABELS[state.pipingColor] || state.pipingColor) + ')';
      parts.push(pipLabel);
    }
    if (state.straps && state.straps !== 'none') parts.push(LABELS[state.straps]);
    if (state.antislip && state.antislip !== 'none') parts.push(LABELS[state.antislip]);
    els.summary.textContent = parts.length ? parts.join(' · ') : 'Configure your T-shape cushion below';
  }

  /* ─── Recalc ─── */
  function recalc() {
    var currentFabric = window.__ucfs_selectedFabric || window._ucfsSelected;
    if (currentFabric && currentFabric !== state.fabric) {
      state.fabric = currentFabric;
    }
    state.length   = parseFloat(els.length.value) || 0;
    state.frontW   = parseFloat(els.frontW.value) || 0;
    state.thickness = parseFloat(els.thickness.value) || 0;
    state.backW    = parseFloat(els.backW.value) || 0;
    state.ear      = parseFloat(els.ear.value) || 0;
    state.extraCovers = els.extraCovers.value;
    state.throwPillow = els.throwPillow ? els.throwPillow.value : 'none';
    state.pillowCovers = els.pillowCovers ? els.pillowCovers.value : 'none';
    state.armrestPillow = els.armrestPillow ? els.armrestPillow.value : 'none';
    state.zipperPlacement = els.zipperPlacement ? els.zipperPlacement.value : '';
    state.pipingColor = els.pipingColor ? els.pipingColor.value : 'match';
    state.notes = els.notes.value;

    if (els.pipingColorWrap) {
      var showPipingColor = (state.piping === 'single' || state.piping === 'double');
      els.pipingColorWrap.style.display = showPipingColor ? '' : 'none';
      if (!showPipingColor) {
        state.pipingColor = 'match';
        if (els.pipingColor) els.pipingColor.value = 'match';
      }
    }

    // Validation highlights
    ['length', 'frontW', 'thickness', 'backW', 'ear'].forEach(function (id) {
      els[id].classList.toggle('ok', parseFloat(els[id].value) > 0);
    });

    // Constraint warning: backW must be > frontW
    if (state.backW && state.frontW && state.backW <= state.frontW) {
      els.backW.style.borderColor = '#e74c3c';
      els.frontW.style.borderColor = '#e74c3c';
    } else {
      els.backW.style.borderColor = '';
      els.frontW.style.borderColor = '';
    }
    // Constraint warning: length must be > ear
    if (state.length && state.ear && state.length <= state.ear) {
      els.length.style.borderColor = '#e74c3c';
      els.ear.style.borderColor = '#e74c3c';
    } else {
      if (!state.backW || !state.frontW || state.backW > state.frontW) {
        els.length.style.borderColor = '';
      }
      els.ear.style.borderColor = '';
    }

    drawCushion();
    updateSummary();

    var dimOk = hasDims();
    var ec = parseInt(state.extraCovers) || 0;

    // Pricing: bounding rectangle = backW × length × thickness
    var base = dimOk ? calcBasePrice(state.backW, state.length, state.thickness) : null;
    var addonTotal = getAddonTotal();

    if (base !== null) {
      var finalPrice = Math.round(base + addonTotal);
      els.priceMain.textContent = '$' + finalPrice;
      var parts = ['Base $' + Math.round(base)];
      if (ADDON.shape[state.shape])       parts.push('Shape +$' + ADDON.shape[state.shape]);
      if (ADDON.piping[state.piping])     parts.push('Piping +$' + ADDON.piping[state.piping]);
      if (ADDON.pipingColor[state.pipingColor]) parts.push('Piping color +$' + ADDON.pipingColor[state.pipingColor]);
      if (ADDON.straps[state.straps])     parts.push('Straps +$' + ADDON.straps[state.straps]);
      if (ADDON.zipper[state.zipper])     parts.push('Zipper +$' + ADDON.zipper[state.zipper]);
      if (ADDON.antislip[state.antislip]) parts.push('Anti-slip +$' + ADDON.antislip[state.antislip]);
      if (ADDON.extraCovers[ec])          parts.push('Covers +$' + ADDON.extraCovers[ec]);
      if (ADDON.throwPillow[state.throwPillow])   parts.push('Pillow +$' + ADDON.throwPillow[state.throwPillow]);
      if (ADDON.pillowCovers[state.pillowCovers]) parts.push('P.Covers +$' + ADDON.pillowCovers[state.pillowCovers]);
      if (ADDON.armrestPillow[state.armrestPillow]) parts.push('Armrest +$' + ADDON.armrestPillow[state.armrestPillow]);
      els.priceBreak.textContent = parts.join(' · ');
    } else {
      els.priceMain.textContent = '$—';
      els.priceBreak.textContent = 'Enter dimensions to see price';
    }

    var complete = isComplete();
    var btns = [els.addBtn, els.addBtnSticky].filter(Boolean);
    btns.forEach(function (btn) {
      btn.disabled = !complete;
      if (complete && base !== null) {
        var fp = Math.round(base + addonTotal);
        btn.textContent = 'Add to order — $' + fp + ' · Free shipping →';
      } else {
        btn.textContent = 'Add to order →';
      }
    });

    if (els.addNote) {
      if (complete) {
        els.addNote.textContent = 'Free shipping · Ships in ~20 business days';
      } else {
        var missing = [];
        if (!state.shape) missing.push('shape');
        if (!dimOk) missing.push('dimensions');
        if (!state.fabric) missing.push('fabric');
        if (!state.piping) missing.push('piping');
        if (!state.straps) missing.push('straps');
        if (!state.antislip) missing.push('anti-slip');
        els.addNote.textContent = missing.length ? 'Select ' + missing.join(', ') + ' to continue' : '';
      }
    }

    document.dispatchEvent(new CustomEvent('configurator:price-updated', {
      detail: {
        price: base !== null ? Math.round(base + addonTotal) : null,
        state: state
      }
    }));
  }

  /* ─── Card selection ─── */
  function pickCard(el, group, value) {
    el.parentElement.querySelectorAll('.cc-opt-card').forEach(function (c) { c.classList.remove('sel'); });
    el.classList.add('sel');
    state[group] = value;
    recalc();
  }

  /* ─── Line properties for cart ─── */
  function getLineProperties() {
    var ec = parseInt(state.extraCovers) || 0;
    var base = calcBasePrice(state.backW, state.length, state.thickness);
    var finalPrice = Math.round(base + getAddonTotal());
    var props = {
      'Shape': LABELS[state.shape] || state.shape,
      'Length': state.length + '"',
      'Front Width': state.frontW + '"',
      'Back Width': state.backW + '"',
      'Ear': state.ear + '"',
      'Thickness': state.thickness + '"',
      'Piping': LABELS[state.piping] || state.piping,
      'Piping Color': (state.pipingColor && state.pipingColor !== 'match') ? (LABELS[state.pipingColor] || state.pipingColor) : 'Match fabric',
      'Straps': LABELS[state.straps] || state.straps,
      'Zipper': LABELS[state.zipper] || state.zipper,
      'Anti-slip': LABELS[state.antislip] || state.antislip
    };
    if (state.fabric) {
      props['Fabric'] = state.fabric.title || '';
      props['_Fabric SKU'] = (state.fabric.variants && state.fabric.variants[0] && state.fabric.variants[0].sku) || '';
      props['_Fabric Handle'] = state.fabric.handle || '';
      props['_Fabric Image'] = (state.fabric.images && state.fabric.images[0] && state.fabric.images[0].src) || '';
    }
    if (ec > 0) props['Extra Covers'] = ec + ' set(s) (+$' + ADDON.extraCovers[ec] + ')';
    if (state.throwPillow && state.throwPillow !== 'none')
      props['Throw Pillow'] = state.throwPillow.replace(/_/g, ' ') + ' (+$' + ADDON.throwPillow[state.throwPillow] + ')';
    if (state.pillowCovers && state.pillowCovers !== 'none')
      props['Pillow Covers'] = state.pillowCovers.replace(/_/g, ' ') + ' (+$' + ADDON.pillowCovers[state.pillowCovers] + ')';
    if (state.armrestPillow && state.armrestPillow !== 'none')
      props['Armrest Pillow'] = state.armrestPillow.replace(/_/g, ' ') + ' (+$' + ADDON.armrestPillow[state.armrestPillow] + ')';
    if (state.zipperPlacement) {
      var ZIP_PLACEMENT_LABELS = { A: 'A — Left side', B: 'B — Back edge', C: 'C — Right side', D: 'D — Front edge (facing you)' };
      props['Zipper Placement'] = ZIP_PLACEMENT_LABELS[state.zipperPlacement] || state.zipperPlacement;
    }
    if (state.notes.trim()) props['Customer Note'] = state.notes.trim();
    props['_Source Product'] = (document.getElementById('cc-configurator') || { dataset: {} }).dataset.productTitle || document.title.split('–')[0].split('—')[0].trim() || location.pathname;

    return { properties: props, price: finalPrice };
  }

  /* ─── DOM transformation ─── */
  function transformDOM() {
    var svg = document.getElementById('cc-dim-svg');
    if (svg) svg.style.maxHeight = '260px';

    // Replace Shape grid with T-shape options
    var shapeGrid = document.getElementById('cc-shape-grid');
    if (shapeGrid) {
      shapeGrid.classList.remove('g4');
      shapeGrid.classList.add('g3');
      shapeGrid.innerHTML = ''
        + '<div class="cc-opt-card" data-group="shape" data-value="tshape">'
        +   '<div class="cc-opt-img"><img src="https://cdn.shopify.com/s/files/1/0676/5687/6232/files/T_Shape_custom_cushion_03e25541-36e4-4580-a5d5-fcd029da1b37.jpg?v=1777270492" alt="T-shape"></div>'
        +   '<div class="cc-opt-name">T-shape</div><div class="cc-opt-price">included</div>'
        + '</div>'
        + '<div class="cc-opt-card" data-group="shape" data-value="tshape_tufted">'
        +   '<div class="cc-opt-img"><img src="https://cdn.shopify.com/s/files/1/0676/5687/6232/files/T_shape_tufted_cushion.webp?v=1783734644" alt="Tufted T-shape"></div>'
        +   '<div class="cc-opt-name">Tufted T-shape</div><div class="cc-opt-price">+$30</div>'
        + '</div>'
        + '<div class="cc-opt-card" data-group="shape" data-value="tshape_france_tufted">'
        +   '<div class="cc-opt-img"><img src="https://cdn.shopify.com/s/files/1/0676/5687/6232/files/T_shape_France_tufted_cushoin.webp?v=1783734644" alt="France tufted T-shape"></div>'
        +   '<div class="cc-opt-name">France tufted T-shape</div><div class="cc-opt-price">+$30</div>'
        + '</div>';
    }

    // cc-dim-l → Length
    var labelL = document.querySelector('label[for="cc-dim-l"]');
    if (labelL) labelL.innerHTML = 'Length (in) <span class="cc-req">*</span>';
    var inputL = document.getElementById('cc-dim-l');
    if (inputL) { inputL.placeholder = 'e.g. 24'; inputL.max = '100'; }

    // cc-dim-w → Front Width
    var labelW = document.querySelector('label[for="cc-dim-w"]');
    if (labelW) labelW.innerHTML = 'Front Width (in) <span class="cc-req">*</span>';
    var inputW = document.getElementById('cc-dim-w');
    if (inputW) { inputW.placeholder = 'e.g. 36'; inputW.max = '200'; }

    // cc-dim-t → Thickness
    var labelT = document.querySelector('label[for="cc-dim-t"]');
    if (labelT) labelT.innerHTML = 'Thickness (in) <span class="cc-req">*</span>';

    // Show back dims row for Back Width + Ear
    var backDims = document.querySelectorAll('.cc-back-dims');
    for (var i = 0; i < backDims.length; i++) {
      var el = backDims[i];
      el.style.display = el.classList.contains('cc-sp-dims') ? 'grid' : 'block';
    }

    // Hide "Back cushion" label
    var backLabel = document.querySelector('.cc-back-label');
    if (backLabel) backLabel.style.display = 'none';

    // cc-back-l → Back Width
    var backLLabel = document.querySelector('label[for="cc-back-l"]');
    if (backLLabel) backLLabel.innerHTML = 'Back Width (in) <span class="cc-req">*</span>';
    var inputBL = document.getElementById('cc-back-l');
    if (inputBL) { inputBL.placeholder = 'e.g. 60'; inputBL.max = '200'; }

    // cc-back-w → Ear
    var backWLabel = document.querySelector('label[for="cc-back-w"]');
    if (backWLabel) backWLabel.innerHTML = 'Ear (in) <span class="cc-req">*</span>';
    var inputBW = document.getElementById('cc-back-w');
    if (inputBW) { inputBW.placeholder = 'e.g. 12'; inputBW.max = '50'; inputBW.style.display = ''; }
    var backWField = inputBW ? inputBW.closest('.cc-field') : null;
    if (backWField) backWField.style.display = '';

    // cc-back-t → hidden (not needed)
    var backT = document.getElementById('cc-back-t');
    if (backT) {
      var backTField = backT.closest('.cc-field');
      if (backTField) backTField.style.display = 'none';
    }

    // Hide price area above SVG
    var spTop = document.querySelector('.cc-sp-top');
    if (spTop) spTop.style.display = 'none';
    var priceBreakDiv = document.getElementById('cc-price-break');
    if (priceBreakDiv) priceBreakDiv.style.display = 'none';

    // Tighten label spacing
    var dimsLabels = document.querySelectorAll('.cc-dims-label');
    for (var j = 0; j < dimsLabels.length; j++) {
      dimsLabels[j].style.marginBottom = '0';
      dimsLabels[j].style.paddingBottom = '0';
    }

    // Move dimension inputs out of sticky container
    var stickyWrap = document.getElementById('cc-sticky-preview');
    if (stickyWrap) {
      var dimsContainer = document.createElement('div');
      dimsContainer.className = 'cc-dims-outside';

      var seatDims = stickyWrap.querySelector('.cc-sp-dims:not(.cc-back-dims)');
      var backDimsEl = stickyWrap.querySelector('.cc-sp-dims.cc-back-dims');
      var hint = stickyWrap.querySelector('.cc-sp-hint');

      if (seatDims) dimsContainer.appendChild(seatDims);
      if (backDimsEl) dimsContainer.appendChild(backDimsEl);
      if (hint) dimsContainer.appendChild(hint);

      stickyWrap.parentNode.insertBefore(dimsContainer, stickyWrap.nextSibling);
    }

    // Renumber steps
    var secHeads = document.querySelectorAll('.cc-sec-head .cc-sn');
    var stepNum = 1;
    for (var k = 0; k < secHeads.length; k++) {
      var sec = secHeads[k].closest('.cc-sec');
      if (sec && sec.style.display !== 'none') {
        secHeads[k].textContent = stepNum++;
      }
    }
  }

  /* ─── Init ─── */
  function init() {
    var wrap = document.getElementById('cc-configurator');
    if (!wrap) return;

    transformDOM();

    els.priceMain    = document.getElementById('cc-price-main');
    els.priceBreak   = document.getElementById('cc-price-break');
    els.summary      = document.getElementById('cc-sp-summary');
    els.length       = document.getElementById('cc-dim-l');
    els.frontW       = document.getElementById('cc-dim-w');
    els.thickness    = document.getElementById('cc-dim-t');
    els.backW        = document.getElementById('cc-back-l');
    els.ear          = document.getElementById('cc-back-w');
    els.extraCovers  = document.getElementById('cc-extra-covers');
    els.throwPillow  = document.getElementById('cc-throw-pillow');
    els.pillowCovers = document.getElementById('cc-pillow-covers');
    els.armrestPillow = document.getElementById('cc-armrest-pillow');
    els.zipperPlacement = document.getElementById('cc-zipper-placement');
    els.pipingColor    = document.getElementById('cc-piping-color');
    els.pipingColorWrap = document.getElementById('cc-piping-color-wrap');
    els.notes        = document.getElementById('cc-notes');
    els.addBtn       = document.getElementById('cc-add-btn');
    els.addBtnSticky = document.getElementById('cc-add-btn-sticky');
    els.addNote      = document.getElementById('cc-add-note');
    els.dimSvg       = document.getElementById('cc-dim-svg');

    wrap.addEventListener('click', function (e) {
      var card = e.target.closest('.cc-opt-card');
      if (!card || !card.dataset.group) return;
      pickCard(card, card.dataset.group, card.dataset.value);
    });

    ['length', 'frontW', 'thickness', 'backW', 'ear'].forEach(function (id) {
      if (els[id]) els[id].addEventListener('input', recalc);
    });
    els.extraCovers.addEventListener('change', recalc);
    if (els.throwPillow) els.throwPillow.addEventListener('change', recalc);
    if (els.pillowCovers) els.pillowCovers.addEventListener('change', recalc);
    if (els.armrestPillow) els.armrestPillow.addEventListener('change', recalc);
    if (els.zipperPlacement) els.zipperPlacement.addEventListener('change', recalc);
    if (els.pipingColor) els.pipingColor.addEventListener('change', recalc);
    els.notes.addEventListener('input', recalc);

    document.addEventListener('fabric:selected', function (e) {
      state.fabric = e.detail;
      recalc();
    });
    var existingFabric = window.__ucfs_selectedFabric || window._ucfsSelected;
    if (!state.fabric && existingFabric) {
      state.fabric = existingFabric;
      recalc();
    }
    setInterval(function () {
      var fab = window.__ucfs_selectedFabric || window._ucfsSelected;
      if (fab && fab !== state.fabric) {
        state.fabric = fab;
        recalc();
      }
    }, 800);

    function handleAdd() {
      if (!isComplete()) return;
      document.dispatchEvent(new CustomEvent('configurator:add-to-cart', { detail: getLineProperties() }));
    }
    if (els.addBtn) els.addBtn.addEventListener('click', handleAdd);
    if (els.addBtnSticky) els.addBtnSticky.addEventListener('click', handleAdd);

    var defaultZipper = wrap.querySelector('.cc-opt-card[data-group="zipper"][data-value="regular"]');
    if (defaultZipper) defaultZipper.classList.add('sel');

    drawCushion();
    recalc();
  }

  window.TShapeCushionConfigurator = {
    getState: function () { return state; },
    getLineProperties: getLineProperties,
    recalc: recalc
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
